import { Text, Line } from '@react-three/drei'
import React, { useRef } from 'react'
import {
  Vector3Tuple,
  Vector3,
  Euler,
  ColorRepresentation,
  Mesh,
  Quaternion,
} from 'three'
import { useFrame } from '@react-three/fiber'

//from k3-veranda
export interface RulerInformation {
  name: string
  color?: ColorRepresentation
  thickness?: number
  lineClamp?: number
  length?: number

  endLines?: RulerHeadLines
  startPoint: Vector3Tuple
  endPoint: Vector3Tuple
  text?: TextInformation
  formatter?: (length: number) => string
}

export interface TextInformation {
  position?: Vector3Tuple
  rotation?: Vector3Tuple
  color?: string
  size?: number
  sprite?: boolean
}

export interface RulerHeadLines {
  length?: number //Länge der Kopflinie
  offset?: number //Position der Kopflinie in Relation zur Ruler Linie
  direction?: Vector3Tuple //Richtungsvektor der Kopflinie
}

const RulerHeadLine = (props: {
  parameters?: RulerHeadLines
  endpoint: Vector3Tuple
  color: ColorRepresentation
  lineWidth: number
}) => {
  const endLength = props.parameters?.length ?? 0.05
  const endStart = (props.parameters?.offset ?? 0.5) * -endLength
  const endEnd = endStart + endLength
  const headLineDirection = props.parameters?.direction ?? [0, 1, 0]
  const headLinePoints = [
    new Vector3(...props.endpoint)
      .add(
        new Vector3(...headLineDirection).normalize().multiplyScalar(endStart)
      )
      .toArray(),
    new Vector3(...props.endpoint)
      .add(new Vector3(...headLineDirection).normalize().multiplyScalar(endEnd))
      .toArray(),
  ]
  return (
    <>
      <Line
        color={props.color}
        lineWidth={props.lineWidth}
        points={headLinePoints}
      />
    </>
  )
}

function Rulertext(props: {
  position: Vector3Tuple
  color: string | undefined
  size: number | undefined
  sprite: boolean
  rotation?: Vector3Tuple
  text: string
}) {
  const ref = useRef<Mesh>(null)
  const textRotation = new Quaternion().setFromEuler(
    new Euler(...(props.rotation ?? [0, 0, 0]))
  )
  useFrame((a) => {
    if (ref.current && props.sprite) {
      ref.current.quaternion.copy(a.camera.quaternion)
    }
  })
  return (
    <Text
      ref={ref}
      position={props.position}
      color={props.color}
      quaternion={textRotation}
      fontSize={props.size}
    >
      {props.text}
    </Text>
  )
}

export const Ruler = (props: RulerInformation) => {
  const getText = props.formatter ?? ((length) => length.toFixed(2))

  const vCon = new Vector3(...props.startPoint).sub(
    new Vector3(...props.endPoint)
  )

  const length = props.length ?? vCon.length()
  const textPosition = (
    props.text?.position
      ? new Vector3(...props.text.position)
      : new Vector3(...props.startPoint).add(
          new Vector3().copy(vCon).multiplyScalar(-0.5)
        )
  ).toArray()

  const lineColor = props.color ?? 'black'
  const lineThickness = props.thickness ?? 1

  const startTo = new Vector3(...props.startPoint)
    .add(new Vector3().copy(vCon).multiplyScalar(-(props.lineClamp ?? 0.5)))
    .toArray()
  const endTo = new Vector3(...props.endPoint)
    .add(new Vector3().copy(vCon).multiplyScalar(props.lineClamp ?? 0.5))
    .toArray()

  return (
    <group>
      <RulerHeadLine
        color={lineColor}
        lineWidth={lineThickness}
        parameters={props.endLines}
        endpoint={props.startPoint}
      />
      <RulerHeadLine
        color={lineColor}
        lineWidth={lineThickness}
        parameters={props.endLines}
        endpoint={props.endPoint}
      />
      <Line
        color={lineColor}
        lineWidth={lineThickness}
        points={[props.startPoint, startTo]}
      />
      <Rulertext
        color={props.text?.color ?? 'black'}
        size={props.text?.size ?? 0.8}
        sprite={props.text?.sprite ?? false}
        position={textPosition}
        text={getText(length)}
        rotation={props.text?.rotation}
      />
      <Line
        color={lineColor}
        lineWidth={lineThickness}
        points={[props.endPoint, endTo]}
      />
    </group>
  )
}
